/**
 * DE PADEN VAN `/api/v1` — ALS DATA, ZONDER ZOD (issue #56, tweede verzoek).
 *
 * Dezelfde paden als in `./endpoints`, maar in een module die op runtime
 * NIETS importeert. Bedoeld voor één consument: `sdk/v1.js`.
 *
 * ── WAAROM DEZE MODULE BESTAAT ──────────────────────────────────────────────
 * §5.2 stap 6 laat het custom element ná `ready` eenmalig
 * `POST /api/v1/embed/verify` doen. Dat pad staat in `./endpoints` (rij
 * `embed-verify`), en de lane-regel van de SDK is dat het embed-protocol niet
 * elders herhaald wordt — dus daar hoort de SDK het te lezen.
 *
 * Alleen: `./endpoints` bouwt op MODULE-NIVEAU zod-schema's
 * (`EndpointKlasseSchema`, `EndpointAuthSchema`, `EndpointSchema` met zijn
 * `superRefine`). ESM-tree-shaking helpt daar niet: die schema's worden bij het
 * laden van de module uitgevoerd, dus één import — wélke export dan ook —
 * trekt zod de bundel in. De SDK-bundel is een IIFE van ~8 KB, §8.2 schrijft
 * "één bestand, geen dependencies" voor, en `packages/embed/bundel-controle.mjs`
 * faalt op elke overgebleven import. Zod erbij is ruwweg een verzesvoudiging
 * van het bestand dat op de pagina van een klant staat — voor één string.
 * Het pad overtypen is de andere kant van dezelfde fout.
 *
 * ── DIT IS GEEN TWEEDE LIJST, HET IS EEN PROJECTIE ──────────────────────────
 * De tabel hieronder staat er letterlijk, maar hij kan niet afdwalen: het
 * `satisfies PadProjectie` eronder is een type dat rechtstreeks uit
 * `ENDPOINTS` wordt afgeleid. Daarmee faalt `tsc` — dus `pnpm typecheck` en
 * CI — op alle drie de manieren waarop zo'n tabel normaal wegloopt:
 *
 *   - een endpoint erbij in `./endpoints` en hier vergeten  →  ontbrekende sleutel
 *   - een sleutel hier die daar niet (meer) bestaat         →  onbekende sleutel
 *   - een pad of alias die van de bron afwijkt              →  verkeerd literal
 *
 * `./endpoints` draagt daarvoor sinds 0.7.0 `as const satisfies readonly
 * Endpoint[]` in plaats van een verbredende `: readonly Endpoint[]`-annotatie;
 * zonder die letterlijke types zou de projectie hieronder verwateren tot
 * `Record<string, { pad: string; alias: string | null }>` en niets meer
 * bewaken. `src/__tests__/endpoint-paden.test.ts` legt dezelfde vergelijking
 * nog een keer op RUNTIME naast elkaar, zodat een toekomstige versoepeling van
 * het type niet stil de bewaking wegneemt.
 *
 * ── DE RICHTING IS DE HELE TRUC ─────────────────────────────────────────────
 * De koppeling loopt via `import type { ENDPOINTS } from "./endpoints"` en
 * daarmee UITSLUITEND door de typelaag. `verbatimModuleSyntax` staat aan
 * (`tsconfig.base.json`), dus een `import type` wordt in zijn geheel uit de
 * emit geschrapt — er blijft geen `import`, geen `require` en geen
 * bare-specifier over. Zou de tabel op runtime uit `ENDPOINTS` worden
 * AFGELEID (een `.map()`, een `Object.fromEntries`), dan zat zod er weer in en
 * was de hele module zinloos.
 *
 * **Laat die import dus `import type` blijven.** Dat is de enige regel van dit
 * bestand, en `src/__tests__/bundelveilig.test.ts` bewaakt hem: die
 * transpileert deze module en loopt de overgebleven importgraaf af — die hoort
 * leeg te zijn.
 *
 * ── WAAROM ER GEEN FUNCTIE IN STAAT ─────────────────────────────────────────
 * Alleen data. Geen `endpointPad(id)`, geen `:id`-invuller, geen vlaglezer:
 * `process.env.PORTAL_API_V1` bestaat niet in een browser, en een resolver die
 * een parameter invult is gedrag en hoort bij de consument (`portalPad` in
 * `packages/embed/src/endpoint-pad.ts` doet dat al voor de serverkant).
 *
 * ── BEIDE KOLOMMEN, WANT DE OVERSTAP IS NOG NIET GEMAAKT ────────────────────
 * `pad` is het v1-pad, `alias` het pad dat vandaag live staat (`null` voor een
 * endpoint dat nooit een oud pad had). Het portaal serveert op dit moment de
 * ALIAS; wie hier alleen `pad` zou lezen, roept een route aan die nog niet
 * bestaat. Welke van de twee je pakt is dus een keuze van de consument en geen
 * eigenschap van het endpoint — vandaar allebei, net als in `./endpoints`.
 */
import type { ENDPOINTS } from "./endpoints";

/* ══════════════════════════════════════════════════════════════════════════
   DE PROJECTIE — het type dat de tabel aan `ENDPOINTS` vastzet
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Per rij van `ENDPOINTS` één sleutel, met exact díé `pad` en `alias` als
 * letterlijk type. Puur typewerk: dit staat niet in de emit.
 */
type PadProjectie = {
  readonly [R in (typeof ENDPOINTS)[number] as R["id"]]: {
    readonly pad: R["pad"];
    readonly alias: R["alias"];
  };
};

/* ══════════════════════════════════════════════════════════════════════════
   DE TABEL
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Het v1-pad en het huidige pad van elk endpoint, in de volgorde van §5.3 —
 * dezelfde volgorde als `ENDPOINTS`, zodat de twee naast elkaar te lezen zijn.
 *
 *     import { ENDPOINT_PADEN } from "@mark2form/contracts/endpoint-paden";
 *     const { pad, alias } = ENDPOINT_PADEN["embed-verify"];
 *
 * Wie ook de klasse, de auth of de CORS-regel nodig heeft, zit niet in een
 * browserbundel en leest `ENDPOINTS` uit `./endpoints`.
 */
export const ENDPOINT_PADEN = {
  /* ── embed-sessie en -config ──────────────────────────────────────────── */
  "embed-session": { pad: "/api/v1/embed/session", alias: "/api/embed-session" },
  "embed-session-refresh": {
    pad: "/api/v1/embed/session/refresh",
    alias: "/api/embed-session-refresh",
  },
  "embed-session-gid": { pad: "/api/v1/embed/session/gid", alias: "/api/embed-session-gid" },
  "embed-session-plaatsing": { pad: "/api/v1/embed/session/plaatsing", alias: null },
  "embed-config": { pad: "/api/v1/embed/config", alias: "/api/embed-config" },
  "embed-startlogo": { pad: "/api/v1/embed/startlogo", alias: null },
  "demo-limiet": { pad: "/api/v1/demo-limiet", alias: "/api/demo-limiet" },
  "embed-meta": { pad: "/api/v1/embed/meta", alias: "/api/embed-meta" },
  "embed-host": { pad: "/api/v1/embed/host", alias: "/api/embed-host" },
  "embed-registry": { pad: "/api/v1/embed/registry", alias: "/api/software-registry" },
  "embed-verify": { pad: "/api/v1/embed/verify", alias: "/api/embed/verify" },
  "embed-legacy-loader": { pad: "/api/v1/embed/legacy/:slug", alias: "/api/embed/:slug" },
  "embed-cart": { pad: "/api/v1/embed/cart", alias: "/api/embed/cart" },

  /* ── gebeurtenissen ───────────────────────────────────────────────────── */
  events: { pad: "/api/v1/events", alias: "/api/event" },

  /* ── verbruik ─────────────────────────────────────────────────────────── */
  usage: { pad: "/api/v1/usage", alias: "/api/usage" },
  "usage-allowance": { pad: "/api/v1/usage/allowance", alias: "/api/usage-allowance" },
  "usage-download": { pad: "/api/v1/usage/download", alias: null },
  "vision-budget-check": {
    pad: "/api/v1/vision-budget/check",
    alias: "/api/portal/vision-budget/check",
  },
  "advisor-usage": { pad: "/api/v1/advisor-usage", alias: "/api/advisor-usage" },

  /* ── ontwerpen, aanvragen en korting ──────────────────────────────────── */
  "designs-create": { pad: "/api/v1/designs", alias: "/api/portal/designs" },
  "designs-read": { pad: "/api/v1/designs/:id", alias: "/api/portal/designs/:id" },
  submissions: { pad: "/api/v1/submissions", alias: "/api/submission" },
  intake: { pad: "/api/v1/intake", alias: null },
  "intake-bijlage": { pad: "/api/v1/intake/bijlage", alias: null },
  "intake-status": { pad: "/api/v1/intake/:id", alias: null },
  "discount-validate": { pad: "/api/v1/discount/validate", alias: "/api/discount/validate" },

  /* ── intern (ops) ─────────────────────────────────────────────────────── */
  "internal-ops-overzicht": { pad: "/api/v1/internal/ops-overzicht", alias: null },

  /* ── intern (runregistratie, W04) ─────────────────────────────────────── */
  "internal-runs-start": { pad: "/api/v1/internal/runs/start", alias: null },
  "internal-runs-einde": { pad: "/api/v1/internal/runs/einde", alias: null },
  "internal-runs-gezondheid": { pad: "/api/v1/internal/runs/gezondheid", alias: null },

  /* ── intern (registratieketen, nazorg A) ──────────────────────────────── */
  "internal-exports": { pad: "/api/v1/internal/exports", alias: null },
  "internal-offertes": { pad: "/api/v1/internal/offertes", alias: null },
  "internal-releases": { pad: "/api/v1/internal/releases", alias: null },

  /* ── intern (Productie-API, §8.3) ─────────────────────────────────────── */
  "internal-api-key-verify": { pad: "/api/v1/internal/api-keys/verify", alias: null },
  "internal-jobs-read": { pad: "/api/v1/internal/jobs/:id", alias: null },
  "internal-productie-sessie": { pad: "/api/v1/internal/productie/sessie", alias: null },

  /* ── de Productie-API zelf (§8.3), op de RUNTIME ──────────────────────── */
  "v1-jobs-create": { pad: "/api/v1/jobs", alias: null },
  "v1-jobs-read": { pad: "/api/v1/jobs/:id", alias: null },
  "v1-jobs-cancel": { pad: "/api/v1/jobs/:id/cancel", alias: null },
  "productie-render": { pad: "/api/v1/internal/productie/render", alias: null },

  /* ── winkelwagenkoppeling (shop-koppelingen, WP K1, §2.5) ─────────────── */
  "offertes-jwks": { pad: "/api/v1/offertes/jwks", alias: null },
  "offertes-maken": { pad: "/api/v1/offertes", alias: null },
  "offertes-betalen": { pad: "/api/v1/offertes/betalen", alias: null },
  "offerte-voorbeeld": { pad: "/api/v1/offertes/:id/voorbeeld", alias: null },
  "offertes-verifieren": { pad: "/api/v1/offertes/verifieren", alias: null },
  "winkel-koppelen-intrekken": { pad: "/api/v1/winkel/koppelen/intrekken", alias: null },
  "winkel-bestellingen": { pad: "/api/v1/winkel/bestellingen", alias: null },
  "winkel-installaties": { pad: "/api/v1/winkel/installaties", alias: null },
  "winkel-koppelen-inwisselen": { pad: "/api/v1/winkel/koppelen/inwisselen", alias: null },

  /* ── publiek (marketingsites) ─────────────────────────────────────────── */
  "public-fonts": { pad: "/api/v1/fonts", alias: null },
  "public-catalog": { pad: "/api/v1/public/catalog", alias: null },
  "public-demo-request": { pad: "/api/v1/public/demo-request", alias: null },
  "public-contact": { pad: "/api/v1/public/contact", alias: null },
} as const satisfies PadProjectie;

/* ══════════════════════════════════════════════════════════════════════════
   DE TYPES ERNAAST
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Een sleutel van `ENDPOINT_PADEN`. Gelijk aan `EndpointId` uit `./endpoints`,
 * maar zonder dat je daarvoor de zod-kant hoeft te importeren — ook niet als
 * type, zodat een consument met één ingang toe kan.
 */
export type EndpointPadId = keyof typeof ENDPOINT_PADEN;

/**
 * Eén rij van de tabel, verbreed tot gewone strings — voor wie er een variabele
 * van wil maken in plaats van een letterlijke opzoeking.
 */
export interface EndpointPadRij {
  /** Het `/api/v1/…`-pad. `:naam` is een padparameter. */
  readonly pad: string;
  /** Het pad dat vandaag live staat, of `null` voor een nieuw endpoint. */
  readonly alias: string | null;
}
